import { Card, SectionLabel, Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';

import { LinkButton, Status } from '@/components/bits';
import { CodeOnHoldNote, useCodesOnHold } from '@/components/CodeOnHold';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import {
  useErrorMessage,
  useFamilyInvites,
  useGroups,
  useInviteFamilies,
  useResendFamilyInvite,
} from '@/data/hooks';
import { monthDay } from '@/lib/time';
import type { ShellContext } from '@/shell/Shell';
import type { FamilyInvite } from '@/types/wire';

/** Addresses, one per line or pasted as a list — the board's own instruction, so commas work too. */
function parseAddresses(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[\n,;]+/)
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
}

const LOOKS_LIKE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * **O09 · /invite-families — the club delivers a capability; it never asserts a relationship.**
 *
 * O05's join code is a capability — whoever holds it may join — and this is the same capability delivered by
 * email instead of read off a poster. What it must never become is a request the family accepts: if a parent
 * taps accept on something a club proposed, the club proposed the relationship and the parent agreed to it,
 * which is weaker consent than a parent choosing to enrol. For a child's data going to a third party that
 * direction is legally load-bearing rather than merely polite.
 *
 * Which is why the subtitle is the first thing on the screen and says what does NOT happen: *"Nothing appears
 * on the roster until they do."*
 *
 * **"Preview" is drawn on the board and is deliberately absent**, for the reason this product deletes dead
 * controls: the preview a director wants is the rendered email, composed on the server, and a second
 * rendering here would be a copy that can disagree with the mail actually sent.
 */
export function InviteFamiliesScreen() {
  const errorMessage = useErrorMessage();
  const { org } = useOutletContext<ShellContext>();
  const groups = useGroups();
  const invites = useFamilyInvites();
  const send = useInviteFamilies();
  const resend = useResendFamilyInvite();
  const onHold = useCodesOnHold();

  const [raw, setRaw] = useState('');
  const [groupId, setGroupId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addresses = useMemo(() => parseAddresses(raw), [raw]);
  const malformed = addresses.filter((a) => !LOOKS_LIKE_EMAIL.test(a));
  const rows = groups.data ?? [];
  // one group means there is nothing to choose; the board draws a picker because most clubs have several
  const chosen = groupId || rows[0]?.id || '';
  const canSend = addresses.length > 0 && malformed.length === 0 && !!chosen;

  const submit = async () => {
    setError(null);
    try {
      await send.mutateAsync({ emails: addresses, groupIds: [chosen], ...(note.trim() ? { note: note.trim() } : {}) });
      setRaw('');
      setNote('');
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const again = async (id: string) => {
    setError(null);
    try {
      await resend.mutateAsync(id);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  if (!org) return <Status testID="invites-loading">{placeholders.empty.loading}</Status>;

  const sent = invites.data ?? [];
  const status = invites.isPending
    ? placeholders.empty.loading
    : invites.error
      ? errorMessage(invites.error)
      : sent.length === 0
        ? deck.o09.empty
        : null;

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{deck.o09.title}</Title2>
          {/* what does NOT happen, first: an invitation creates nothing until the parent consents */}
          <Text variant="bodySm" tone="muted">
            {deck.o09.subtitle}
          </Text>
        </div>
      </div>

      <section className="stack" data-testid="send-section">
        <SectionLabel>{deck.o09.send}</SectionLabel>
        <Card variant="quiet" radius="row" padding={18}>
          <div className="stack">
            {/*
             * While billing stops a club taking on families, say so first, with the button that fixes it, rather than
             * after a director has typed a list and pressed Send (the 23 Oct audit, T2-7). The banner's own words.
             */}
            {onHold ? (
              <div className="code-on-hold" data-testid="invite-on-hold">
                <Text variant="bodySm" tone="ink">
                  {onHold === 'paused' ? placeholders.billing.paused : placeholders.billing.setUp}
                </Text>
                <CodeOnHoldNote reason={onHold} withoutLine testID="invite-on-hold-note" />
              </div>
            ) : null}
            <label className="field-label" htmlFor="o09-emails">
              {deck.o09.emails}
            </label>
            <textarea
              id="o09-emails"
              className="script-text"
              value={raw}
              placeholder={deck.o09.emailsHelper}
              onChange={(e) => setRaw(e.target.value)}
              data-testid="invite-emails"
            />
            {/*
             * **The promise that makes this screen safe, said to the person it constrains.** A club that could
             * tell whether an address has an account would learn, about a household it has no relationship
             * with, whether that household uses this app.
             */}
            <Text variant="bodySm" tone="muted" testID="no-account-hint">
              {deck.o09.noAccountHint}
            </Text>
            {malformed.length ? (
              <Status testID="bad-addresses">{deck.o09.notAnEmail(malformed[0]!)}</Status>
            ) : null}

            <label className="field-label" htmlFor="o09-group">
              {deck.o09.joining}
            </label>
            <select
              id="o09-group"
              // styled as every other select in the portal, not the browser's grey box (the 23 Oct audit, T2-7)
              className="field-select"
              value={chosen}
              onChange={(e) => setGroupId(e.target.value)}
              data-testid="invite-group"
            >
              {rows.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>

            <TextField
              label={deck.o09.note}
              value={note}
              onChangeText={setNote}
              testID="invite-note"
            />
            {error ? <Status testID="invite-error">{error}</Status> : null}
            {/* absent until it would do something, the same rule as O06's Publish and O08's em dash */}
            {canSend ? (
              <div className="actions">
                <WasabiButton
                  label={deck.o09.sendCta(addresses.length)}
                  size="sm"
                  fullWidth={false}
                  onPress={submit}
                  testID="send-invitations"
                />
              </div>
            ) : null}
          </div>
        </Card>
      </section>

      <section className="stack" data-testid="sent-section">
        <div className="page-header">
          <SectionLabel>{deck.o09.sent}</SectionLabel>
          <Text variant="bodySm" tone="muted">
            {deck.o09.expiry}
          </Text>
        </div>
        {status ? (
          <Status testID="invites-status">{status}</Status>
        ) : (
          <Card variant="quiet" radius="row" padding={18}>
            <div className="stack">
              {sent.map((i: FamilyInvite) => (
                <div className="staff-row" key={i.id} data-testid={`invite-${i.id}`}>
                  <div className="stack-tight">
                    <Text variant="body" tone="ink">
                      {i.email}
                    </Text>
                    <Text variant="bodySm" tone="muted">
                      {monthDay(i.sentAt)}
                    </Text>
                  </div>
                  <Text variant="bodySm" tone="muted" testID={`state-${i.id}`}>
                    {deck.o09.state(i.state)}
                  </Text>
                  {/*
                   * **Resend on every row, including a joined one? No — and there is no Revoke anywhere.**
                   * Resending to a family that has joined would mail them a link to consent to something they
                   * already did. Withdrawing is P21's, and it belongs to the parent.
                   */}
                  {i.state === 'joined' ? (
                    <Text variant="bodySm" tone="muted" testID={`no-resend-${i.id}`}>
                      —
                    </Text>
                  ) : (
                    <LinkButton
                      label={deck.o09.resend}
                      onPress={() => again(i.id)}
                      testID={`resend-${i.id}`}
                    />
                  )}
                </div>
              ))}
              {/* what each state does NOT mean: "Invited" on a dashboard reads as progress */}
              <Text variant="bodySm" tone="muted" testID="invite-explainer">
                {deck.o09.explainer}
              </Text>
            </div>
          </Card>
        )}
      </section>
    </>
  );
}
